import React from 'react';

import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';

import type { ChannelMarkupTesterPanelProps } from './ChannelMarkupTesterPanel.types';
import ChannelMarkupTesterResultCard from './ChannelMarkupTesterResultCard';

type Props = Pick<
  ChannelMarkupTesterPanelProps,
  | 'testerMarkupMode'
  | 'setTesterMarkupMode'
  | 'onRunPreview'
  | 'currencyOptions'
  | 'selectedCurrencies'
  | 'setSelectedCurrencies'
  | 'isClientInitReady'
  | 'isClientInitFetching'
  | 'handleRefreshClientInit'
  | 'clientInitConfig'
  | 'testerResults'
  | 'testerResult'
  | 'testerValues'
  | 'effectiveTesterContext'
> & { isGeneralModeUnavailable: boolean };

export default function ChannelMarkupTesterRunSection({
  testerMarkupMode,
  setTesterMarkupMode,
  onRunPreview,
  currencyOptions,
  selectedCurrencies,
  setSelectedCurrencies,
  isClientInitReady,
  isClientInitFetching,
  handleRefreshClientInit,
  isGeneralModeUnavailable,
  clientInitConfig,
  testerResults,
  testerResult,
  testerValues,
  effectiveTesterContext,
}: Props) {
  const toggleCurrency = (currency: string) =>
    setSelectedCurrencies((current) =>
      current.includes(currency) ? current.filter((item) => item !== currency) : [...current, currency],
    );
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            3. Run preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Evaluate the current draft against the selected preview region and inspect the winning markup result.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            select
            label="Markup source"
            value={testerMarkupMode}
            onChange={(event) => {
              const nextMode = event.target.value === 'general' ? 'general' : 'current';
              setTesterMarkupMode(nextMode);
              if (nextMode === 'general' && !isClientInitReady && !isClientInitFetching) {
                void handleRefreshClientInit();
              }
            }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="current">Legacy current-draft round</MenuItem>
            <MenuItem value="general" disabled={!isClientInitReady}>
              Customer portal (select then evaluate)
            </MenuItem>
          </TextField>
          <Button variant="contained" onClick={onRunPreview} disabled={isGeneralModeUnavailable}>
            Check
          </Button>
          {testerMarkupMode === 'current' && !isClientInitReady ? (
            <Button
              size="small"
              variant="text"
              onClick={() => void handleRefreshClientInit()}
              disabled={isClientInitFetching}
            >
              Load client-init
            </Button>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Evaluates {testerResults.length} scenario{testerResults.length === 1 ? '' : 's'}.
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Currency preview chips
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {currencyOptions.map((option) => {
              const isSelected = selectedCurrencies.includes(option.key);
              return (
                <Chip
                  key={option.key}
                  label={option.displayName}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => toggleCurrency(option.key)}
                  onDelete={isSelected ? () => toggleCurrency(option.key) : undefined}
                />
              );
            })}
          </Stack>
          {selectedCurrencies.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Select one or more currencies to run the preview.
            </Typography>
          ) : null}
        </Stack>
        {isGeneralModeUnavailable && !clientInitConfig ? (
          <Alert severity="warning" variant="outlined">
            Load client init before checking general mode. General evaluation stays on the current draft until the
            client init snapshot is loaded.
          </Alert>
        ) : null}
        <Alert severity={testerResult === 'matched' ? 'success' : 'info'} variant="outlined">
          {testerResult === 'matched'
            ? 'A markup matched the selected preview region.'
            : testerResult === 'pending'
              ? 'Evaluation is pending for at least one scenario.'
              : 'No markup matched the selected preview region.'}
        </Alert>
        <Stack spacing={2}>
          {testerResults.map((result) => (
            <ChannelMarkupTesterResultCard
              key={result.currency}
              result={result}
              testerValues={testerValues}
              effectiveTesterContext={effectiveTesterContext}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
